<template>
  <!-- Loading Screen -->
  <LoadingScreen
    v-if="isInitialLoading"
    title="Loading Dashboard"
    subtitle="Setting up your workspace..."
  />

  <!-- Bootstrap failed: profile could not be loaded -->
  <div
    v-else-if="hasBootstrapError"
    class="flex h-screen w-full flex-col items-center justify-center gap-4 bg-gray-50 px-6 text-center"
  >
    <div class="flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
      <i class="pi pi-exclamation-triangle text-2xl text-primary"></i>
    </div>
    <div>
      <h1 class="text-lg font-semibold text-gray-800">We couldn't load your dashboard</h1>
      <p class="mt-1 max-w-sm text-sm text-gray-500">
        Check your connection and try again. If this keeps happening, contact your school administrator.
      </p>
    </div>
    <div class="flex items-center gap-2">
      <Button label="Try Again" icon="pi pi-refresh" :loading="retrying" @click="retryBootstrap" />
      <Button label="Log Out" icon="pi pi-sign-out" severity="secondary" outlined @click="confirmLogout" />
    </div>
  </div>

  <div v-else class="flex h-screen overflow-hidden bg-white w-full relative">

    <!-- Mobile Sidebar -->
    <Drawer v-model:visible="navVisible" :baseZIndex="1000" class="w-72 lg:hidden p-0">
        <template #header>
            <div class="flex items-center gap-2">
                 <img v-if="schoolLogo" :src="schoolLogo" alt="School logo" class="w-8 h-8 object-contain" />
                 <i v-else class="pi pi-graduation-cap text-primary" style="font-size: 1.5rem;"></i>
                 <span class="font-bold text-lg text-gray-800 truncate">{{ schoolName || 'School' }}</span>
            </div>
        </template>
        <StudentMobileView :hideMenu="hideMenu" />
    </Drawer>

    <!-- Desktop Sidebar -->
    <aside :class="[
      'hidden lg:flex flex-col overflow-y-hidden duration-300 ease-linear border-r border-gray-200 z-20 bg-white',
      isCollapsed ? 'w-[80px] items-center' : 'w-56'
    ]" class="h-screen transition-all">
      <div class="flex items-center justify-between gap-2 px-6 py-5.5 lg:py-6.5 h-[80px] shrink-0">
        <div class="flex gap-2 items-center self-stretch my-auto overflow-hidden">
            <i class="pi pi-graduation-cap text-primary"  :class="isCollapsed ? 'text-2xl' : 'text-3xl'"></i>
            <div v-if="!isCollapsed" class="my-auto font-bold text-lg truncate w-[180px]" :title="schoolName">{{schoolName}}</div>
        </div>
      </div>

      <div class="no-scrollbar flex flex-col overflow-y-auto duration-300 ease-linear flex-1">
        <div class="flex flex-col w-full px-2">
          <div v-if="!isCollapsed"
            class="gap-2 self-stretch px-4 py-4 w-full text-xs font-semibold tracking-wider text-gray-400 uppercase">
            MAIN MENU
          </div>
         <ul class="flex flex-col justify-center gap-1 w-full pb-10">
          <SidebarMenuItem
            v-for="(item, index) in filteredStudentMenuItems"
            :key="index"
            :routeName="item.routeName"
            :tooltipText="item.tooltipText"
            :menuText="item.menuText"
            :menuIcon="item.menuIcon"
            :isCollapsed="isCollapsed"
            :isActiveRoute="isActiveRoute(item.path)"
             :school="school || null"
          />

        <!-- Logout -->
        <li
          @click="showLogoutDialog = true"
          class="flex gap-3 items-center px-4 py-3 mt-4 w-full rounded-lg cursor-pointer text-gray-800 transition-all duration-200 hover:bg-red-50 hover:text-red-600"
          v-tooltip.right="isCollapsed ? 'Log Out' : null"
        >
          <i class="pi pi-sign-out text-red-600 text-lg"></i>
          <span
            v-if="!isCollapsed"
            class="flex-1 shrink self-stretch my-auto text-sm font-medium leading-none basis-0 text-red-600"
          >
            Log Out
          </span>
        </li>
      </ul>

        </div>

      </div>
    </aside>

    <!-- Main Content -->
    <div class="relative flex flex-1 flex-col overflow-y-auto overflow-x-hidden h-full w-full bg-gray-50">

        <!-- Global Top Toolbar -->
        <Toolbar class="bg-white shadow-sm px-4 py-3 sticky top-0 z-30 border-b border-gray-200">
            <template #start>
                <div class="flex items-center gap-3">
                    <!-- Mobile Toggle -->
                    <div
                        @click="showMenu"
                        class="flex lg:hidden items-center justify-center w-10 h-10 rounded-full bg-primary/10 text-primary cursor-pointer transition-colors"
                    >
                        <i class="pi pi-bars text-lg"></i>
                    </div>

                    <!-- Desktop Toggle (New) -->
                     <div
                        @click="toggleSidebar"
                        class="hidden lg:flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 text-primary cursor-pointer hover:bg-primary/10 transition-colors"
                    >
                        <i class="pi pi-bars text-lg"></i>
                    </div>

                    <!-- Title/Welcome (Dynamic) -->
                     <div class="flex flex-col">
                        <span class="font-bold text-gray-800 text-lg truncate max-w-[200px] lg:max-w-none">{{ routeNameMap[route.name as string] || 'Dashboard' }}</span>
                         <span class="text-xs text-gray-500 hidden lg:block" v-if="studentProfile">Welcome Back, {{ studentProfile?.first_name }}</span>
                    </div>
                </div>
            </template>

            <template #end>
                <div class="flex items-center gap-2 md:gap-4">
                  <!-- Tour Trigger (tablet+) — hidden on CBT-only portals -->
                  <div v-if="!userStore.isCbtOnlyStudentPortal" class="hidden md:flex">
                    <TourTriggerButton />
                  </div>

                  <!-- Notification Widget — hidden on CBT-only portals -->
                  <NotificationWidget v-if="!userStore.isCbtOnlyStudentPortal" />

                  <!-- Download Manager — hidden on CBT-only portals -->
                  <DownloadManager v-if="!userStore.isCbtOnlyStudentPortal" />

                  <!-- Page guide video (only on pages with a registered guide) -->
                  <VideoHelpButton />

                  <!-- Active Calendar Badge -->
                  <AcademicCalendarDisplay />

                  <!-- Avatar Button -->
                  <Button
                    icon=""
                    class="flex items-center justify-center w-10 h-10 rounded-full bg-primary text-white font-semibold uppercase focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                    @click="togglePanel($event)"
                  >
                    {{ studentInitials }}
                  </Button>

                 <!-- OverlayPanel for Student Details -->
                <Popover
                  ref="panel"
                  dismissable
                  showCloseIcon
                  class="w-72 rounded-xl shadow-lg"
                >
                  <!-- Header -->
                  <div class="flex items-center gap-3 pb-3 border-b border-gray-200">
                    <!-- Avatar -->
                    <div
                      class="w-10 h-10 rounded-full bg-primary text-white flex items-center justify-center font-semibold text-sm uppercase"
                    >
                      {{ studentInitials }}
                    </div>

                    <div class="overflow-hidden">
                      <p class="text-sm font-semibold text-gray-800 truncate">
                        {{ studentProfile?.first_name }} {{ studentProfile?.surname }}
                      </p>
                      <p class="text-xs text-gray-500 truncate">
                        {{ studentProfile?.matric_no }}
                      </p>
                    </div>
                  </div>

                  <!-- Details -->
                  <div class="mt-3 space-y-2 text-sm">
                    <div class="flex items-center gap-2 text-gray-600">
                      <i class="pi pi-users text-xs"></i>
                      <span>{{ currentClassroomLabel }}</span>
                    </div>

                    <div v-if="currentProgrammeLabel" class="flex items-center gap-2 text-gray-600">
                      <i class="pi pi-book text-xs"></i>
                      <span>{{ currentProgrammeLabel }}</span>
                    </div>
                  </div>
                </Popover>
                </div>
            </template>
        </Toolbar>

      <div class="flex-1 w-full max-w-[100vw] pb-16 lg:pb-0">
        <AnnouncementBanner />
        <router-view> </router-view>
      </div>
      <!--
        Must-read school notices. Mounted at the shell rather than per-route so
        it covers every student screen — a gate the student can walk around by
        navigating is not a gate. It renders nothing when nothing is owed.
      -->
      <AnnouncementGate />
      <StudentDataRequestGate />
    </div>

    <Dialog v-model:visible="showLogoutDialog" modal header="Confirm Logout" :style="{ width: '90vw', maxWidth: '350px' }">
      <div class="mb-4">Are you sure you want to log out?</div>
      <div class="flex justify-end gap-2">
        <button class="px-4 py-2 rounded bg-gray-200 hover:bg-gray-300" @click="showLogoutDialog = false">Cancel</button>
        <button class="px-4 py-2 rounded bg-red-600 text-white hover:bg-red-700" @click="confirmLogout">Logout</button>
      </div>
    </Dialog>

    <TicketingWidget />
    <ConnectivityBanner />
    <!-- Global AI assistant: floating launcher + slide-over chat, on every student page.
         Offset above TicketingWidget (bottom-6) and MobileBottomNav (bottom-0). -->
    <AiAssistantWidget launcherPosition="bottom-24 right-6" />
    <MobileBottomNav :school-type="school?.school_type" @open-menu="showMenu" />
  </div>
</template>

<script setup lang="ts">
import StudentMobileView from './StudentMobileView.vue';
import TicketingWidget from '@/components/common/TicketingWidget.vue';
import NotificationWidget from '@/components/common/NotificationWidget.vue';
import AnnouncementBanner from '@/components/common/AnnouncementBanner.vue';
import AnnouncementGate from '@/components/StudentDashboard/Announcements/AnnouncementGate.vue';
import StudentDataRequestGate from '@/components/StudentDashboard/DataRequests/StudentDataRequestGate.vue';
import DownloadManager from '@/components/common/DownloadManager.vue';
import AcademicCalendarDisplay from '@/components/DashBoard/common/AcademicCalendarDisplay.vue';
import TourTriggerButton from '@/components/common/TourTriggerButton.vue';
import { useUserStore } from '@/store/userStore';
import { useNotificationStore } from '@/store/notificationStore';
import { useToast } from 'primevue/usetoast';
import { useUserQuery } from '@/composables/queries/useUserQuery';
import { useBranding } from '@/composables/useBranding';
import { studentMenuItems } from '@/constants/route-details.constant';
import { filterStudentMenuItems } from '@/utils/studentPortalMode';
import SidebarMenuItem from '@/components/common/SidebarMenuItem.vue';
import AiAssistantWidget from '@/components/common/AiAssistantWidget.vue';
import { useSubscription } from '@vue/apollo-composable';
import { ON_USER_NOTIFICATION } from '@/graphql/subscriptions/tickets';
import LoadingScreen from '@/components/common/LoadingScreen.vue';
import { provideAppTour } from '@/composables/tour/useAppTour';

provideAppTour();

const route = useRoute();
const userStore = useUserStore();
const notifStore = useNotificationStore();
const router = useRouter();
const toast = useToast();
const showLogoutDialog = ref(false);

const {
  studentProfile,
  school,
  studentregcurrentsemester,
  currentClassroomSection,
  studentAcademicCalendar,
  errorUser,
  refetchUser,
} = useUserQuery();

const { schoolLogo } = useBranding();

const navVisible = ref(false);

const showMenu = () => {
  navVisible.value = true;
};
const hideMenu = () => { navVisible.value = false; };

// Two distinct concerns, two distinct keys: `openMobileNav` opens the drawer on
// small screens, `toggleSidebar` collapses the desktop rail (provided below).
provide('openMobileNav', showMenu);

const schoolName = computed(() =>
  studentProfile.value?.currentClassroomSection?.school?.name || undefined,
);

const routeNameMap: Record<string, string> = {
  'student-home': 'Overview',
  'student-timetable': 'Timetable',
  'student-courses': 'Course Registration',
  'student-course-reg': 'Course Registration',
  'student-result': 'Results',
  'student-settings': 'Settings',
  'student-payments': 'Payments',
  'student-financials': 'Financials',
  'student-cbt': 'CBT',
  'student-billing': 'Billing',
  'student-transcripts': 'My Transcripts',
  'student-eclass': 'E-Class',
  'student-attendance': 'Attendance',
  'student-tickets': 'Support Tickets',
  'student-ticket-detail': 'Ticket Details',
  'student-forms': 'Forms',
  'student-form-fill': 'Fill Form',
};

const studentInitials = computed(() => {
  const p = studentProfile.value;
  if (!p) return '';
  const f = p.first_name?.[0] || '';
  const s = p.surname?.[0] || '';
  return (f + s).toUpperCase();
});

const currentClassroomLabel = computed(() => {
  const section = currentClassroomSection.value;
  const classroomName = section?.classroom?.name;
  if (classroomName) {
    const isTertiary = studentProfile.value?.currentClassroomSection?.school?.school_type?.toLowerCase() === 'tertiary';
    return isTertiary ? `${classroomName} Level` : classroomName;
  }
  return section?.name || 'Classroom';
});

const currentProgrammeLabel = computed(() => {
  const prog = studentAcademicCalendar.value?.programme;
  if (!prog) return null;
  return prog.degree ? `${prog.program_name} (${prog.degree})` : prog.program_name;
});

const panel = ref();
const togglePanel = (event: Event) => {
  panel.value.toggle(event);
};

const isActiveRoute = (path: string) => route.path === path || route.path.startsWith(path);
const isCollapsed = ref(false);

// Method to toggle sidebar state
const toggleSidebar = () => {
  isCollapsed.value = !isCollapsed.value;
};

const checkViewportSize = () => {
  isCollapsed.value = window.innerWidth < 1400;
};

onMounted(() => {
  checkViewportSize();
  window.addEventListener('resize', checkViewportSize);
});

onUnmounted(() => {
  window.removeEventListener('resize', checkViewportSize);
});

const filteredStudentMenuItems = computed(() => filterStudentMenuItems(studentMenuItems, {
  schoolType: school.value?.school_type,
  cbtOnly: userStore.isCbtOnlyStudentPortal,
}));

// Use the logout method from userStore
const handleLogout = async() => {
  try {
    await userStore.logout(); // Call the logout method from userStore
    // Use hard redirect to fully reset all Vue state, Apollo cache, and stores
    window.location.href = '/login';
  } catch (error) {
    toast.add({ severity: 'error', summary: 'Error', detail: error instanceof Error ? error.message : 'Error during logout', life: 3000 });
  }
};

const confirmLogout = async() => {
  showLogoutDialog.value = false;
  await handleLogout();
};

// Provide the state and toggle function
provide('isCollapsed', isCollapsed);
provide('toggleSidebar', toggleSidebar);

// --- Bootstrap state ---
// Show the loading screen only while we are actually waiting. If the profile
// query fails we surface an error with a retry instead of spinning forever.

const hasBootstrapError = computed(() => !!errorUser.value && !studentProfile.value);
const isInitialLoading = computed(
  () => !hasBootstrapError.value && (!studentProfile.value || !school.value),
);

const retrying = ref(false);
const retryBootstrap = async() => {
  retrying.value = true;
  try {
    await refetchUser();
  } finally {
    retrying.value = false;
  }
};

// Global Notifications

// For Students, we subscribe to student.{id}
const studentId = computed(() => studentProfile.value?.id);
// Only subscribe if we have a user
const { onResult: onNotification } = useSubscription(ON_USER_NOTIFICATION, () => ({
  id: studentId.value,
  type: 'student',
}), {
  enabled: computed(() => !!studentId.value),
});

onNotification((result) => {
  if (result.data?.userNotification) {
    const notif = result.data.userNotification;

    // Add to store. The persisted id (when the backend stored the row) is what
    // the hydrated history keys on, so the live copy dedupes instead of showing
    // twice, and can be marked read server-side (#224).
    const persisted = !!notif.id;

    notifStore.addNotification({
      id: persisted ? String(notif.id) : `local-${Date.now().toString()}`,
      title: notif.title,
      message: notif.message,
      type: notif.type || 'system',
      read: !!notif.read_at,
      created_at: notif.created_at || new Date().toISOString(),
      metadata: (notif.metadata || {}) as Record<string, unknown>,
      persisted,
    });

    // Show Toast
    toast.add({
      severity: 'info',
      summary: 'Support Update',
      detail: notif.message,
      life: 5000,
      group: 'br', // Bottom right or default
    });
  }
});

</script>

<style>
/* Chrome, Safari and Opera */
.no-scrollbar::-webkit-scrollbar {
  display: none;
}

.no-scrollbar {
  -ms-overflow-style: none;
  /* IE and Edge */
  scrollbar-width: none;
  /* Firefox */
}

.aside {
  transition: width 0.3s ease;
}
</style>
